import { authApi } from "#/api/authApi";
import { Button, Loader, Text } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["verify-email", token],
    queryFn: async () => {
      if (!token) {
        throw new Error("Verification token is missing");
      }
      const response = await authApi.verifyEmail(token);
      return response.data;
    },
    enabled: !!token && !verificationAttempted,
    retry: false,
  });

  useEffect(() => {
    if (data || isError) {
      setVerificationAttempted(true);
    }
  }, [data, isError]);

  const isSuccess = !!data && !isError;
  const errorMessage = error instanceof Error ? error.message : "Verification failed";

  return (
    <div className="w-screen min-h-screen flex">
      <img
        src="/login.jpg"
        className="object-cover h-full w-full absolute z-0 top-0 left-0"
        alt="bg"
      />

      <div className="w-full h-full absolute top-0 left-0 z-10 flex items-center justify-center">
        <div className="w-full max-w-md bg-white/95 backdrop-blur-sm rounded-lg p-8 shadow-lg mx-4">
          {!token ? (
            <div className="flex flex-col items-center gap-6">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
                <IconX size={32} color="white" />
              </div>
              <h1 className="text-2xl font-bold text-center text-gray-900">
                {t("auth.verifyEmail.invalidLink")}
              </h1>
              <Text c="dimmed" ta="center">
                {t("auth.verifyEmail.invalidLinkDescription")}
              </Text>
              <Link to="/login" className="w-full">
                <Button fullWidth color="black" size="md">
                  {t("auth.verifyEmail.goToLogin")}
                </Button>
              </Link>
            </div>
          ) : isLoading ? (
            <div className="flex flex-col items-center gap-6">
              <Loader size="lg" color="black" />
              <h1 className="text-2xl font-bold text-center text-gray-900">
                {t("auth.verifyEmail.verifyingEmail")}
              </h1>
              <Text c="dimmed" ta="center">
                {t("auth.verifyEmail.verifyingEmailDescription")}
              </Text>
            </div>
          ) : isSuccess ? (
            <div className="flex flex-col items-center gap-6">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center">
                <IconCheck size={32} color="white" />
              </div>
              <h1 className="text-2xl font-bold text-center text-gray-900">
                {t("auth.verifyEmail.emailVerified")}
              </h1>
              <Text c="dimmed" ta="center">
                {t("auth.verifyEmail.emailVerifiedDescription")}
              </Text>
              <Link to="/login" className="w-full">
                <Button fullWidth color="black" size="md">
                  {t("auth.verifyEmail.proceedToLogin")}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-6">
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
                <IconX size={32} color="white" />
              </div>
              <h1 className="text-2xl font-bold text-center text-gray-900">
                {t("auth.verifyEmail.verificationFailed")}
              </h1>
              <Text c="dimmed" ta="center">
                {errorMessage}
              </Text>
              <Text c="dimmed" ta="center" size="sm">
                {t("auth.verifyEmail.verificationFailedDescription")}
              </Text>
              <Link to="/login" className="w-full">
                <Button fullWidth color="black" size="md">
                  {t("auth.verifyEmail.goToLogin")}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
